import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Save, Loader2, CheckCircle2, Bot, Brain } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AISettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openaiConfigured, setOpenaiConfigured] = useState(false);
  const [openaiMasked, setOpenaiMasked] = useState("");
  const [openaiKey, setOpenaiKey] = useState("");
  const [anthropicConfigured, setAnthropicConfigured] = useState(false);
  const [anthropicMasked, setAnthropicMasked] = useState("");
  const [anthropicKey, setAnthropicKey] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  async function fetchSettings() {
    try {
      const res = await fetch(API_URL + "/api/organization/ai-settings", { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setOpenaiConfigured(d.openai_configured);
        setOpenaiMasked(d.openai_key_masked || "");
        setAnthropicConfigured(d.anthropic_configured);
        setAnthropicMasked(d.anthropic_key_masked || "");
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const body = {};
      if (openaiKey.trim()) body.openai_key = openaiKey.trim();
      if (anthropicKey.trim()) body.anthropic_key = anthropicKey.trim();
      const res = await fetch(API_URL + "/api/organization/ai-settings", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const d = await res.json();
        setOpenaiConfigured(d.openai_configured);
        setOpenaiMasked(d.openai_key_masked || "");
        setAnthropicConfigured(d.anthropic_configured);
        setAnthropicMasked(d.anthropic_key_masked || "");
        setOpenaiKey("");
        setAnthropicKey("");
        toast.success("Clés IA enregistrées");
      } else {
        const err = await res.json();
        toast.error(typeof err.detail === "string" ? err.detail : "Erreur");
      }
    } catch (e) { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  }

  async function handleClear(provider) {
    setSaving(true);
    try {
      const body = {};
      if (provider === "openai") body.openai_key = "__CLEAR__";
      if (provider === "anthropic") body.anthropic_key = "__CLEAR__";
      const res = await fetch(API_URL + "/api/organization/ai-settings", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const d = await res.json();
        setOpenaiConfigured(d.openai_configured);
        setOpenaiMasked(d.openai_key_masked || "");
        setAnthropicConfigured(d.anthropic_configured);
        setAnthropicMasked(d.anthropic_key_masked || "");
        toast.success("Clé supprimée — utilisation de la clé par défaut");
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setSaving(false); }
  }

  return (
    <Card data-testid="ai-settings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Clés IA (Claude &amp; GPT)
        </CardTitle>
        <CardDescription>
          Utilisez vos propres clés API pour les modèles IA. Si aucune clé n'est renseignée, une clé partagée par défaut est utilisée.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* OpenAI / GPT */}
            <div className="space-y-2">
              <Label htmlFor="openai-key" className="flex items-center gap-2">
                <Bot className="w-4 h-4" /> Clé OpenAI (GPT)
              </Label>
              <Input
                id="openai-key"
                type="password"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder={openaiConfigured ? "Clé enregistrée : " + openaiMasked + " (laisser vide pour conserver)" : "sk-..."}
                data-testid="openai-key-input"
              />
              <div className="flex items-center justify-between">
                {openaiConfigured ? (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Clé enregistrée ({openaiMasked})
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Aucune clé — clé par défaut utilisée</p>
                )}
                {openaiConfigured && (
                  <button
                    type="button"
                    onClick={() => handleClear("openai")}
                    className="text-xs text-destructive hover:underline"
                    data-testid="clear-openai-key-btn"
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>

            {/* Anthropic / Claude */}
            <div className="space-y-2">
              <Label htmlFor="anthropic-key" className="flex items-center gap-2">
                <Brain className="w-4 h-4" /> Clé Anthropic (Claude)
              </Label>
              <Input
                id="anthropic-key"
                type="password"
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
                placeholder={anthropicConfigured ? "Clé enregistrée : " + anthropicMasked + " (laisser vide pour conserver)" : "sk-ant-..."}
                data-testid="anthropic-key-input"
              />
              <div className="flex items-center justify-between">
                {anthropicConfigured ? (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Clé enregistrée ({anthropicMasked})
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Aucune clé — clé par défaut utilisée</p>
                )}
                {anthropicConfigured && (
                  <button
                    type="button"
                    onClick={() => handleClear("anthropic")}
                    className="text-xs text-destructive hover:underline"
                    data-testid="clear-anthropic-key-btn"
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} data-testid="save-ai-settings-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
